import { useContext, useState, useRef, useLayoutEffect } from "react";
import { AuthContext } from "../AuthContext";
import { Link } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import Logo from "./Logo";
import { useTranslation } from "../i18n/LanguageContext";
import { FEATURES } from "../config/features";
import { useChatNotifications } from "../contexts/ChatNotificationsContext";

function Navbar() {
  const { user, isAdmin } = useContext(AuthContext);
  const { language, setLanguage, t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const chatNotifications = useChatNotifications();
  const totalUnread = chatNotifications?.totalUnread || 0;
  const navRef = useRef(null);

  // Publish the navbar's real rendered height as a CSS variable so
  // full-viewport-height pages below it (Chat) can size themselves as
  // "available viewport minus navbar", not a brittle hardcoded pixel value
  // or a naive 100dvh that would run taller than the visible area and hide
  // the composer behind the browser's bottom chrome. Re-measures whenever
  // the navbar's own height changes (e.g. mobile menu opening/closing, or a
  // rotation/resize), via ResizeObserver rather than only once on mount.
  useLayoutEffect(() => {
    const el = navRef.current;
    if (!el) return undefined;
    const setVar = () => {
      document.documentElement.style.setProperty("--navbar-h", `${el.offsetHeight}px`);
    };
    setVar();
    const observer = new ResizeObserver(setVar);
    observer.observe(el);
    return () => observer.disconnect();
  }, [menuOpen]);

  const handleLogout = async () => {
    await signOut(auth);
    setMenuOpen(false);
  };

  const navLinkClass =
    "block rounded-lg px-3 py-2 transition-all duration-200 hover:bg-slate-800 hover:scale-[1.02]";

  // Small red unread-count badge, shared by the desktop and mobile Buddies
  // links. Capped visually at "9+" so it never stretches the nav item.
  const UnreadBadge = ({ count }) =>
    count > 0 ? (
      <span className="ml-1.5 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
        {count > 9 ? "9+" : count}
      </span>
    ) : null;

  return (
    <nav ref={navRef} className="relative bg-blue-950/95 backdrop-blur-sm text-white shadow-xl border-b border-slate-400/20 sticky top-0 z-30">
      <div className="flex items-center justify-between px-4 py-2.5">
        <Link
          to="/"
          className="flex items-center gap-2 hover:text-blue-300 transition"
          onClick={() => setMenuOpen(false)}
        >
          <Logo className="h-11 w-11 sm:h-12 sm:w-12" />
          <span className="font-bold text-lg sm:text-xl">PartyBuddy</span>
        </Link>

        {/* Desktop nav — unchanged from the original layout, hidden on mobile */}
        <div className="hidden md:flex flex-wrap gap-2 items-center">
          <span className="rounded-full border border-sky-500/60 bg-slate-900/80 px-3 py-1 text-[11px] uppercase tracking-[0.25em] text-sky-200">{FEATURES.phase}</span>
          {user && isAdmin && (
            <span className="rounded-full border border-amber-400/70 bg-amber-500/20 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.25em] text-amber-200">Admin</span>
          )}
          {user ? (
            <>
              <Link to="/" className="hover:bg-slate-800 px-3 py-2 rounded-lg transition-all duration-200 hover:scale-105">{t('nav.events', 'Events')}</Link>
              <Link to="/events" className="hover:bg-slate-800 px-3 py-2 rounded-lg transition-all duration-200 hover:scale-105">{t('nav.news', 'News & Reviews')}</Link>
              <Link to="/buddies" className="hover:bg-slate-800 px-3 py-2 rounded-lg transition-all duration-200 hover:scale-105 inline-flex items-center">{t('nav.buddies', 'Buddies')}<UnreadBadge count={totalUnread} /></Link>
              <Link to="/profile" className="hover:bg-slate-800 px-3 py-2 rounded-lg transition-all duration-200 hover:scale-105">{t('nav.profile', 'Profile')}</Link>
              {/* Discreet, low-emphasis language control — kept next to the
                  account controls rather than styled like a primary nav item. */}
              <div className="flex items-center overflow-hidden rounded-full border border-slate-700/70 text-[11px] text-slate-400" role="group" aria-label={t('nav.language', 'Language')}>
                <button
                  type="button"
                  onClick={() => setLanguage('de')}
                  className={`px-2 py-1 transition ${language === 'de' ? 'bg-slate-700 text-slate-100' : 'hover:text-slate-200'}`}
                >
                  DE
                </button>
                <button
                  type="button"
                  onClick={() => setLanguage('en')}
                  className={`px-2 py-1 transition ${language === 'en' ? 'bg-slate-700 text-slate-100' : 'hover:text-slate-200'}`}
                >
                  EN
                </button>
              </div>
              <button onClick={handleLogout} className="hover:bg-red-800 px-3 py-2 rounded-lg transition-all duration-200 hover:scale-105">{t('nav.logout', 'Logout')}</button>
            </>
          ) : (
            <>
              <Link to="/auth" className="hover:bg-slate-800 px-3 py-2 rounded-lg transition-all duration-200 hover:scale-105">{t('nav.login', 'Login')}</Link>
              <div className="flex items-center overflow-hidden rounded-full border border-slate-700/70 text-[11px] text-slate-400" role="group" aria-label={t('nav.language', 'Language')}>
                <button
                  type="button"
                  onClick={() => setLanguage('de')}
                  className={`px-2 py-1 transition ${language === 'de' ? 'bg-slate-700 text-slate-100' : 'hover:text-slate-200'}`}
                >
                  DE
                </button>
                <button
                  type="button"
                  onClick={() => setLanguage('en')}
                  className={`px-2 py-1 transition ${language === 'en' ? 'bg-slate-700 text-slate-100' : 'hover:text-slate-200'}`}
                >
                  EN
                </button>
              </div>
            </>
          )}
        </div>

        {/* Mobile hamburger toggle — hidden on desktop */}
        <button
          type="button"
          onClick={() => setMenuOpen((prev) => !prev)}
          className="relative md:hidden inline-flex items-center justify-center rounded-lg border border-slate-700 bg-slate-800/90 p-2 text-white shadow-inner transition hover:bg-slate-700"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
        >
          {!menuOpen && totalUnread > 0 && (
            <span className="absolute -right-1 -top-1 h-3 w-3 rounded-full bg-red-500 ring-2 ring-slate-900" aria-hidden="true" />
          )}
          {menuOpen ? (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>
      </div>

      {/* Mobile menu panel */}
      {menuOpen && (
        <div className="md:hidden border-t border-slate-400/20 bg-blue-950/98 backdrop-blur-sm px-4 pb-4 pt-3 shadow-xl">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="rounded-full border border-sky-500/60 bg-slate-900/80 px-3 py-1 text-[11px] uppercase tracking-[0.25em] text-sky-200">{FEATURES.phase}</span>
            {user && isAdmin && (
              <span className="rounded-full border border-amber-400/70 bg-amber-500/20 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.25em] text-amber-200">Admin</span>
            )}
          </div>
          <label className="mb-3 flex items-center justify-between gap-2">
            <span className="text-[11px] uppercase tracking-[0.2em] text-slate-400">{t('nav.language', 'Language')}</span>
            <div className="flex items-center overflow-hidden rounded-full border border-slate-700/70 text-xs text-slate-400">
              <button
                type="button"
                onClick={() => setLanguage('de')}
                className={`px-3 py-1 transition ${language === 'de' ? 'bg-slate-700 text-slate-100' : 'hover:text-slate-200'}`}
              >
                DE
              </button>
              <button
                type="button"
                onClick={() => setLanguage('en')}
                className={`px-3 py-1 transition ${language === 'en' ? 'bg-slate-700 text-slate-100' : 'hover:text-slate-200'}`}
              >
                EN
              </button>
            </div>
          </label>
          <div className="flex flex-col gap-1">
            {user ? (
              <>
                <Link to="/" className={navLinkClass} onClick={() => setMenuOpen(false)}>{t('nav.events', 'Events')}</Link>
                <Link to="/events" className={navLinkClass} onClick={() => setMenuOpen(false)}>{t('nav.news', 'News & Reviews')}</Link>
                <Link to="/buddies" className={`${navLinkClass} inline-flex items-center`} onClick={() => setMenuOpen(false)}>{t('nav.buddies', 'Buddies')}<UnreadBadge count={totalUnread} /></Link>
                <Link to="/profile" className={navLinkClass} onClick={() => setMenuOpen(false)}>{t('nav.profile', 'Profile')}</Link>
                <button onClick={handleLogout} className={`${navLinkClass} w-full text-left hover:bg-red-800`}>{t('nav.logout', 'Logout')}</button>
              </>
            ) : (
              <Link to="/auth" className={navLinkClass} onClick={() => setMenuOpen(false)}>{t('nav.login', 'Login')}</Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}

export default Navbar;